import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchAdmin, toErrorMessage } from '../../admin/api';
import { PATH_DASHBOARD } from '../../routes/paths';
import { AdminActionBar, AdminDetailPanel, AdminLoadingState, AdminScreenLayout } from '../../sections/admin/shared';

function OverviewCard({ title, value, caption }) {
  return (
    <AdminDetailPanel
      title={title}
      items={[
        { label: 'Value', value: `${value ?? 0}` },
        { label: 'Meaning', value: caption },
      ]}
    />
  );
}

OverviewCard.propTypes = {
  caption: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default function SosOverviewPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: false,
    error: null,
    data: null,
  });

  const loadOverview = useCallback(async () => {
    setState({
      loading: true,
      error: null,
      data: null,
    });

    try {
      const response = await fetchAdmin('/sos/overview');
      setState({
        loading: false,
        error: null,
        data: response?.overview || null,
      });
    } catch (error) {
      setState({
        loading: false,
        error: toErrorMessage(error),
        data: null,
      });
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const overview = state.data;

  return (
    <AdminScreenLayout
      title="SOS Overview"
      breadcrumbs={[
        { name: 'Admin', href: PATH_DASHBOARD.root },
        { name: 'SOS' },
        { name: 'Overview' },
      ]}
    >
      <AdminActionBar
        title="SOS Dashboard"
        description="This overview uses the lightweight aggregate SOS endpoint for page-load cards and periodic refresh."
        actions={[
          {
            label: 'Refresh',
            variant: 'contained',
            icon: 'solar:refresh-bold',
          },
          {
            label: 'Open Signals',
            variant: 'outlined',
            icon: 'solar:list-bold',
          },
        ]}
        onActionClick={(action) => {
          if (action.label === 'Refresh') {
            loadOverview();
          }

          if (action.label === 'Open Signals') {
            navigate(PATH_DASHBOARD.sos.signals);
          }
        }}
      />

      {state.loading ? <AdminLoadingState rows={4} /> : null}

      {!state.loading && state.error ? (
        <Alert severity="error" variant="outlined">
          {state.error}
        </Alert>
      ) : null}

      {!state.loading && !state.error && overview ? (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={4}>
              <OverviewCard title="Total Incidents" value={overview.total_incidents} caption="All SOS signal records." />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <OverviewCard title="Broadcast Escalated" value={overview.broadcast_escalated_incidents} caption="Signals that reached the contact-broadcast threshold." />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <OverviewCard title="Active Outward Blocks" value={overview.active_outward_transfer_blocks} caption="Distinct users currently under an active SOS-created outward-transfer block." />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <OverviewCard title="Active With Cases" value={overview.active_incidents_with_cases} caption="Active SOS signals already linked to admin review cases." />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <OverviewCard title="Active Broadcast Escalated" value={overview.active_broadcast_escalated_incidents} caption="Active signals already escalated to contact broadcast." />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <OverviewCard title="Highest Distress Level" value={overview.highest_distress_level} caption="Current maximum distress level in the SOS signal table." />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <AdminDetailPanel
                title="Current Queue Stages"
                items={[
                  { label: 'First attempt only', value: `${overview.first_attempt_only_incidents ?? 0}` },
                  { label: 'Second attempt stage', value: `${overview.second_attempt_incidents ?? 0}` },
                  { label: 'Active incidents', value: `${overview.active_incidents ?? 0}` },
                  { label: 'Resolved incidents', value: `${overview.resolved_incidents ?? 0}` },
                ]}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AdminDetailPanel
                title="Overview Endpoint Notes"
                items={[
                  { label: 'Source', value: 'GET /v2/admin/sos/overview' },
                  { label: 'Use case', value: 'Page-load dashboard cards and periodic admin refresh' },
                  { label: 'Query strategy', value: 'Aggregate queries only, with no timeline joins or event-table scans' },
                  { label: 'Primary flow', value: 'Use this page for KPIs, then move into the Signals page for incident review' },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <AdminDetailPanel
                title="Admin Test Simulation"
                items={[
                  { label: 'Seed command', value: 'php spark app:simulate_sos' },
                  { label: 'Seed command with count', value: 'php spark app:simulate_sos 10' },
                  { label: 'Safety behavior', value: 'Seeds synthetic SOS rows directly and does not trigger live broadcast SMS' },
                  { label: 'Testing coverage', value: 'Creates mixed active and resolved incidents, distress stages, blocks, linked cases, and timelines' },
                ]}
              />
            </Grid>
          </Grid>
        </>
      ) : null}
    </AdminScreenLayout>
  );
}
